import React, { useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { GridSelectionModel } from '@material-ui/data-grid';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';
import { uniqBy, groupBy } from 'lodash';
import {
  useWaitlistBulkIncomingVolunteersFetch,
  useRejectActivityApplicationFetch,
  useApproveActivityApplicationFetch,
  useApproveBulkActivityApplicationFetch,
  useWaitlistIncomingVolunteerFetch,
  useRejectBulkActivityApplicationFetch,
} from '../../activity-management/activity-form-v2/activity-form-actions-v2';
import { TemplateSnackbar, SnackBarOptions } from '../../../../common/snackbars/TemplateSnackbar';
import { ApplicationActions, VolunteerApplicationMenuButton } from './VolunteerApplicationsMenu';
import { ApplicationRemovingMessage } from './ApplicationRemovingMessage';
import {
  ConfirmAddMaxVolunteer,
  ITeam,
} from '../../activities/activity-timeline/past/report/form/ConfirmAddMaxVolunteer';
import { usePendingActivityRequests } from '../common/pending-requests/usePendingActivityRequests';
import { PendingRequestsFilterBar } from '../common/pending-requests/PendingRequestsFilterBar';
import { PendingRequestsGrid } from '../common/pending-requests/PendingRequestsGrid';
import { PendingRequestsHeader } from '../common/pending-requests/PendingRequestsHeader';
import { downloadPendingRequestsCsv } from '../common/pending-requests/pending-requests-csv';
import { PendingRequestRow, getVolunteerName } from '../common/pending-requests/pending-requests-types';

export interface Application {
  id: string;
  program: string;
  profile: {
    profileId: string;
    userId: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
    preferredName: string;
    email: string | null;
    contactNumber: string | null;
  };
  volunteerId: string;
  activityName: string;
  activityId: string;
  programId: string;
  dateApplied: DateTime;
  volunteerName: string;
  maxTeam: number | null;
  totalEnrolments: number;
  email: string;
  phone: string;
  applicationAnswers: Array<{ answer: string; question: { activityApplicationQuestionId: string; label: string } }>;
}

const toTeamMember = (row: PendingRequestRow) => ({
  activityId: row.activityId,
  activityName: row.activityName,
  volunteerId: row.volunteerId,
  volunteerName: getVolunteerName(row),
});

export function VolunteersApplicationsScreen() {
  const requests = usePendingActivityRequests('activity-applications');
  const { rows, refetch } = requests;

  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [snackbar, setSnackbar] = useState<SnackBarOptions>();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [teamExceed, setTeamExceed] = useState(false);
  const [exceedingTeams, setExceedingTeams] = useState<ITeam>();
  const maxTeamConfirm = useRef<() => void>();
  const maxTeamCancel = useRef<() => void>();

  const moveToWaitlist = useWaitlistIncomingVolunteerFetch();
  const moveAllToWaitlist = useWaitlistBulkIncomingVolunteersFetch();
  const approveActivityApplication = useApproveActivityApplicationFetch();
  const approveBulkActivityApplication = useApproveBulkActivityApplicationFetch();
  const removeApplication = useRejectActivityApplicationFetch();
  const removeAllApplication = useRejectBulkActivityApplicationFetch();

  const selectedIds = selectionModel as string[];
  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const selectedVolunteers = uniqBy(selectedRows, (row) => row.volunteerId);
  const selectedActivities = uniqBy(selectedRows, (row) => row.activityId);

  const volunteerText =
    selectedVolunteers.length === 1
      ? getVolunteerName(selectedVolunteers[0])
      : `${selectedVolunteers.length} volunteers`;
  const activityText =
    selectedActivities.length === 1 ? selectedActivities[0].activityName : `${selectedActivities.length} activities`;

  const onActionComplete = (message: string) => {
    setSelectionModel([]);
    refetch();
    setSnackbar({ open: true, variant: 'success', message });
  };

  const confirmIfTeamsExceeded = (teams: ITeam, onConfirm: () => void) => {
    if (Object.keys(teams).length === 0) {
      onConfirm();
      return;
    }
    setExceedingTeams(teams);
    setTeamExceed(true);
    maxTeamConfirm.current = () => {
      onConfirm();
      setTeamExceed(false);
      setExceedingTeams(undefined);
    };
    maxTeamCancel.current = () => setTeamExceed(false);
  };

  const addToTeam = (row: PendingRequestRow) => {
    const exceeds = row.maxTeam !== null && row.maxTeam > 0 && row.totalEnrolments >= row.maxTeam;
    confirmIfTeamsExceeded(exceeds ? { [row.activityId]: [toTeamMember(row)] } : {}, () =>
      approveActivityApplication
        .run({ activityApplicationId: row.id })
        .then(() => onActionComplete('Volunteer added to team successfully'))
    );
  };

  const bulkAddToTeam = () => {
    const byActivity = groupBy(selectedRows, (row) => row.activityId);
    const exceeding = Object.keys(byActivity).reduce<ITeam>((acc, activityId) => {
      const activityRows = byActivity[activityId];
      const { maxTeam, totalEnrolments } = activityRows[0];
      return maxTeam && maxTeam < activityRows.length + totalEnrolments
        ? { ...acc, [activityId]: activityRows.map(toTeamMember) }
        : acc;
    }, {});
    const count = selectedIds.length;

    confirmIfTeamsExceeded(exceeding, () =>
      approveBulkActivityApplication
        .run({ activityApplicationIds: selectedIds })
        .then(() => onActionComplete(`Added ${count} volunteers to team successfully`))
    );
  };

  const onRemove = () => {
    const count = selectedIds.length;
    const request =
      count === 1
        ? removeApplication.run({ activityApplicationId: selectedIds[0] })
        : removeAllApplication.run({ activityApplicationIds: selectedIds });

    request.then(() => {
      setShowRemoveDialog(false);
      onActionComplete(count === 1 ? 'Application removed' : `Removed ${count} applications successfully`);
    });
  };

  const onAction = (action: ApplicationActions, row: PendingRequestRow) => {
    switch (action) {
      case 'add':
        addToTeam(row);
        break;
      case 'remove':
        setSelectionModel([row.id]);
        setShowRemoveDialog(true);
        break;
      case 'moveToWaitlist':
        moveToWaitlist
          .run({ activityApplicationId: row.id })
          .then(() => onActionComplete('Waitlisted application successfully'));
        break;
      default:
        break;
    }
  };

  const onActionBulk = (action: ApplicationActions) => {
    switch (action) {
      case 'add':
        bulkAddToTeam();
        break;
      case 'remove':
        setShowRemoveDialog(true);
        break;
      case 'moveToWaitlist': {
        const count = selectedIds.length;
        moveAllToWaitlist
          .run({ activityApplicationIds: selectedIds })
          .then(() => onActionComplete(`Waitlisted ${count} applications successfully`));
        break;
      }
      default:
        break;
    }
  };

  const onDownload = () => {
    requests
      .fetchAllRows()
      .then((allRows) => downloadPendingRequestsCsv(allRows, 'Date Applied', 'volunteers_applications'))
      .catch(() => setSnackbar({ open: true, variant: 'error', message: 'Unable to download CSV' }));
  };

  return (
    <Box display='flex' flexDirection='column' flex={1} height='100%' overflow='hidden'>
      <PendingRequestsHeader
        total={requests.total}
        noun={pluralize('Activity Application', requests.total)}
        selectedCount={selectedRows.length}
        bulkActions={<VolunteerApplicationMenuButton onAction={onActionBulk} bulk />}
        onDownload={onDownload}
        downloading={requests.downloading}
      />
      <Box display='flex' flexDirection='column' flex={1} overflow='auto' minHeight={0}>
        <PendingRequestsFilterBar
          filters={requests.filters}
          filterOptions={requests.filterOptions}
          onChange={(changes) => {
            setSelectionModel([]);
            requests.setFilters(changes);
          }}
          onReset={() => {
            setSelectionModel([]);
            requests.resetFilters();
          }}
          dateLabel='Date Applied'
        />
        <PendingRequestsGrid
          rows={rows}
          total={requests.total}
          page={requests.page}
          onPageChange={(page) => {
            setSelectionModel([]);
            requests.setPage(page);
          }}
          loading={requests.loading}
          error={requests.error}
          onRetry={refetch}
          orderBy={requests.orderBy}
          orderAscending={requests.orderAscending}
          onSort={(field) => {
            setSelectionModel([]);
            requests.toggleSort(field);
          }}
          dateLabel='Date Applied'
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
          renderActions={(row) => (
            <VolunteerApplicationMenuButton
              onAction={(action: ApplicationActions) => onAction(action, row)}
              bulk={false}
            />
          )}
        />
      </Box>
      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
      <ApplicationRemovingMessage
        open={selectedRows.length > 0 && showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onRemove={onRemove}
        title={`Remove: ${volunteerText} from ${activityText}`}
        warningText={`You are removing ${pluralize('this', selectedVolunteers.length)} volunteer's activity ${pluralize(
          'application',
          selectedVolunteers.length
        )}`}
      />
      {teamExceed && maxTeamCancel.current && maxTeamConfirm.current && (
        <ConfirmAddMaxVolunteer onCancel={maxTeamCancel.current} onOK={maxTeamConfirm.current} teams={exceedingTeams} />
      )}
    </Box>
  );
}
